import React from 'react';
import { Item, useCanvasStore } from '@/stores/canvasStore';
import { useDragToBoardStore } from '@/stores/dragToBoardStore';
import { getFolderColorFromValue } from '@/lib/folderColors';

interface BoardCardProps {
  item: Item;
  isSelected?: boolean;
}

// macOS-style folder path - tab on left side with rounded corners
const FOLDER_PATH = "M8 4 Q4 4 4 8 L4 82 Q4 86 8 86 L112 86 Q116 86 116 82 L116 18 Q116 14 112 14 L44 14 Q40 14 38 10 L36 6 Q34 4 30 4 Z";
const FOLDER_FRONT_PATH = "M4 22 Q4 18 8 18 L112 18 Q116 18 116 22 L116 82 Q116 86 112 86 L8 86 Q4 86 4 82 Z";

export function BoardCard({ item, isSelected = false }: BoardCardProps) {
  const content = item.content as { 
    linked_board_id?: string; 
    name?: string;
    color?: string;
  };
  const { boards } = useCanvasStore();
  const { hoverBoardId, hoverPhase, hoverProgress, isDragging } = useDragToBoardStore();

  const isHovered = isDragging && hoverBoardId === content.linked_board_id;
  const isHolding = isHovered && (hoverPhase === 'holding' || hoverPhase === 'opening');
  const isOpening = isHovered && hoverPhase === 'opening';

  // Get the color palette for this folder
  const colorPalette = getFolderColorFromValue(content.color);

  // Calculate scale based on state
  const getScale = () => {
    if (isOpening) return 1.08;
    if (isHovered) return 1.04;
    return 1;
  };

  return (
    <div
      className={`w-full h-full relative transition-all duration-200 ${isHolding ? 'animate-pulse' : ''}`}
      style={{
        transform: `scale(${getScale()})`,
        transformOrigin: 'center center',
        zIndex: isHovered ? 100 : 1,
      }}
    >
      {/* Glow effect when hovered during drag */}
      {isHovered && (
        <div 
          className="absolute -inset-2 rounded-xl opacity-40 blur-lg transition-opacity duration-300 animate-pulse"
          style={{ backgroundColor: colorPalette.folder }}
        />
      )}

      {/* macOS-style Folder SVG */}
      <svg
        viewBox="0 0 120 90"
        className="w-full h-full drop-shadow-md transition-all duration-200"
        style={{
          filter: isHovered ? `drop-shadow(0 8px 16px ${colorPalette.folder}60)` : undefined,
        }}
      >
        {/* Selection outline - follows folder shape */}
        {isSelected && (
          <path
            d={FOLDER_PATH}
            fill="none"
            stroke="hsl(var(--partnr))"
            strokeWidth="2"
            strokeLinejoin="round"
            className="transition-all duration-150"
          />
        )}
        
        {/* Folder back (darker) - the tab part */}
        <path
          d={FOLDER_PATH}
          fill={colorPalette.folderDark}
        />
        {/* Folder front (main color) */}
        <path
          d={FOLDER_FRONT_PATH}
          fill={colorPalette.folder}
        />
        {/* Subtle gradient overlay for depth */}
        <defs>
          <linearGradient id={`folderGradient-${item.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="white" stopOpacity="0.12" />
            <stop offset="100%" stopColor="black" stopOpacity="0.08" />
          </linearGradient>
        </defs>
        <path
          d={FOLDER_FRONT_PATH}
          fill={`url(#folderGradient-${item.id})`}
        />
      </svg>

      {/* Name label pill - positioned at bottom right */}
      <div 
        className="absolute bottom-[12%] right-[6%] px-2.5 py-1 rounded-full text-xs font-medium truncate max-w-[85%] shadow-sm"
        style={{
          backgroundColor: colorPalette.label,
          color: colorPalette.labelText,
        }}
      >
        {content.name || 'Untitled'}
      </div>

      {/* Circular progress indicator when holding */}
      {isHolding && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="relative w-12 h-12">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 48 48">
              <circle
                cx="24"
                cy="24"
                r="20"
                fill="none"
                stroke="white"
                strokeWidth="3"
                opacity="0.3"
              />
              <circle
                cx="24"
                cy="24"
                r="20"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${(hoverProgress / 100) * 125.66} 125.66`}
                className="transition-all duration-100"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-bold text-white drop-shadow-md">
                {Math.round(hoverProgress)}%
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Status text overlay when hovered during drag */}
      {isHovered && hoverPhase === 'hovering' && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-xs font-medium text-white drop-shadow-md bg-black/30 px-2 py-1 rounded">
            Release to drop
          </span>
        </div>
      )}
    </div>
  );
}
